import wixWindowFrontend from 'wix-window-frontend'
import wixLocationFrontend from 'wix-location-frontend'
import { session } from 'wix-storage-frontend'

export const createSeoDescription = (description, minLength, maxLengh, ellipsis = '…') => {
    if (!description) return ''
    const descriptionWoLineBreaks = description.replace(/(\r\n|\n|\r)/gm, ' ')
    if (!descriptionWoLineBreaks || descriptionWoLineBreaks.length <= maxLengh) return descriptionWoLineBreaks

    const truncatedToSentence = truncateToSentence(descriptionWoLineBreaks, maxLengh)
    return (truncatedToSentence.length >= minLength) ?
        truncatedToSentence :
        truncateToWord(descriptionWoLineBreaks, maxLengh, ellipsis)
}

const truncateToSentence = (text, maxLength) => {
    if (!text) return ''
    const sentences = text.match(/[^\.!\?。]+[\.!\?。]+/g) //match ".","!","?","。" - English and Japanese ending sentence punctuation
    if (!sentences) return text

    let truncatedText = ''
    for (const sentence of sentences) {
        if ((truncatedText + sentence).length > maxLength) break;
        truncatedText += sentence
    }

    return truncatedText
}

const truncateToWord = (text, maxLength, ellipsis) => {
    if (!text) return ''
    if (text.length <= maxLength) return text
    const truncatedText = text.substr(0, maxLength - 1)
    const lastSpaceIndex = truncatedText.lastIndexOf(' ')
    const truncatedToWord = truncatedText.substr(0, Math.min(truncatedText.length, lastSpaceIndex))

    return `${truncatedToWord}${ellipsis}`
}

export const isValidHttpUrl = (string) => {
    try {
        const newUrl = new URL(string);
        return newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
    } catch (err) {
        return false;
    }
}

export const getImageData = (image) => {
    if (!image) return 'https://static.wixstatic.com/media/c5ccda_1070277bcb6f4b18af6af4474e7a248e~mv2.png'
    const match = image.match(/(?:wix:image:\/\/v\d+\/)([a-zA-Z0-9_~]+\.jpg).*#originWidth=(\d+)&originHeight=(\d+)/)
    if (!match) return { url: image }
    const [, fileName, width, height] = match
    const url = `https://static.wixstatic.com/media/${fileName}`
    return { fileName, url, width, height }
}

export const slugifyUrl = (string) => {
    const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;'
    const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------'
    const p = new RegExp(a.split('').join('|'), 'g')

    return string.toString().toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with -
        .replace(p, c => b.charAt(a.indexOf(c))) // Replace special characters
        .replace(/&/g, '-and-') // Replace & with 'and'
        .replace(/[^\w\-]+/g, '') // Remove all non-word characters
        .replace(/\-\-+/g, '-') // Replace multiple - with single -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, '') // Trim - from end of text
}

/**
 * Link a $w(#TextElement), either the whole text or just a partial
 * @name linkText
 * @function
 * @param {object} $textElement - $w(#TextElement) to add link to
 * @param {string} url - URL to add
 * @param {options} options
 * 
 * 
 * @typedef {Object} options
 * @property {string} [target=_self] - Either '_self' or '_blank'
 * @property {string} [linkSegment] - If present, will only link the supplied string, otherwise the whole text.
 * @property {boolean} [underlineLink=false] - If true, links will be marked with an underline.

 * @returns Void
 *
 *
 * @example
 * // Example usage of the function
 * linkText($w('#textWithLink'), https://www.wix.com, {target: '_self', linkSegment: 'click here'})
 * 
 * @see {@link https://www.wix.com/liorwi/mkt-packages/velo-packages/mkt-utils MKT Utils Package README}
 */
export const linkText = ($textElement, url, options = { target: '_self', linkSegment: undefined, underlineLink: false }) => {
    const { target, linkSegment, underlineLink } = options

    const html = $textElement.html
    const thereIsHrefAlready = html.indexOf('href=') > -1
    if (thereIsHrefAlready) {
        const currentUrl = html.substring(html.lastIndexOf("href=") + 5).split(' ')[0]
        $textElement.html = html.replace(currentUrl, url)
        return
    }

    let htmlToLink = linkSegment
    if (!htmlToLink) {
        const regex = /<(p|h[1-6])[^>]*>(.*?)<\/\1>/i
        const match = html.match(regex)
        htmlToLink = match ? match[2] : null
    }

    if (!htmlToLink) return

    const aHrefOpenTag = underlineLink ? `<a href=${url} target=${target} style='text-decoration:underline;'>` : `<a href=${url} target=${target}>`

    const linkedHtml = `${aHrefOpenTag}${htmlToLink}</a>`
    $textElement.html = $textElement.html.replace(htmlToLink, linkedHtml)
}

/**
 * Saves the URL of the current page as the previous page in session storage. Call this function from the masterPage.js
 * @function
 * @returns {void}
 */
export const savePreviousPage = () => {
    if (wixWindowFrontend.rendering.env !== 'browser') return

    const previousPage = session.getItem('currentPage')
    const currentPage = wixLocationFrontend.url

    if (previousPage === currentPage) return

    if (previousPage) session.setItem('previousPage', previousPage)
    session.setItem('currentPage', wixLocationFrontend.url)
}

/**
 * Returns the URL of the previous page within a Wix site or the referrer URL.
 * @function
 * @returns {string|null} - The URL of the previous page.
 */
export const getPreviousPage = () => {
    if (wixWindowFrontend.rendering.env === 'browser') {
        const previousPage = session.getItem('previousPage')
        return previousPage || wixWindowFrontend.referrer || wixLocationFrontend.baseUrl
    } else {
        return null
    }
}